import { EventEnd } from "./EulerStates/EventEnd";
import { EventPlaying } from "./EulerStates/EventPlaying";
import { GetNextEvent } from "./EulerStates/GetNextEvent";
import { LoadAndShowOdds } from "./EulerStates/LoadAndShowOdds";
import { NoMoreBets } from "./EulerStates/NoMoreBets";
import { UpdatePlaying } from "./EulerStates/UpdatePlaying";
import { BaseState } from "./States/BaseState";
import { EulerStateEvents, EulerStates } from "./Consts";
import { ISubscribableEvent } from "./ISubscribableEvent";
import { GetCurrentEvent } from "./EulerStates/GetCurrentEvent";

///This class is a singleton that various game states.
class EulerStateManager implements ISubscribableEvent {
  private static instance: EulerStateManager | null = null;

  // Private constructor to prevent external instantiation
  private constructor() {
    this.eulerGetCurrentEventState = new GetCurrentEvent(this);
    this.eulerGetNextEventState = new GetNextEvent(this);
    this.eulerLoadAndShowOddsState = new LoadAndShowOdds(this);
    this.eulerNoMoreBetsState = new NoMoreBets(this);
    this.eulerUpdatePlayingState = new UpdatePlaying(this);
    this.eulerEventPlayingState = new EventPlaying(this);
    this.eulerEventEndState = new EventEnd(this);
  }

  // Method to create or get the singleton instance
  public static getInstance(): EulerStateManager {
    if (EulerStateManager.instance === null) {
      EulerStateManager.instance = new EulerStateManager();
    }
    return EulerStateManager.instance;
  }

  private currentState: BaseState<EulerStateManager> | null = null;

  // Various game states
  //Flow: GetNextEvent->LoadAndShowOdds->UpdatePlaying->EventPlaying->EventEnd
  public eulerGetCurrentEventState: BaseState<EulerStateManager>;
  public eulerGetNextEventState: BaseState<EulerStateManager>;
  public eulerLoadAndShowOddsState: BaseState<EulerStateManager>;
  public eulerNoMoreBetsState: BaseState<EulerStateManager>;
  public eulerUpdatePlayingState: BaseState<EulerStateManager>;
  public eulerEventPlayingState: BaseState<EulerStateManager>;
  public eulerEventEndState: BaseState<EulerStateManager>;

  // Event listener arrays for each state
  private onEnterGetCurrentEventStateListeners: (() => void)[] = [];
  private onEnterGetNextEventStateListeners: (() => void)[] = [];
  private onEnterLoadAndShowsOddsStateListeners: (() => void)[] = [];
  private onEnterNoMoreBetsStateListeners: (() => void)[] = [];
  private onEnterUpdatePlayingStateListeners: (() => void)[] = [];
  private onEnterEventPlayingStateListeners: (() => void)[] = [];
  private onEnterEventEndStateListeners: (() => void)[] = [];

  /**
   * Allows the subscription of an event listener to a specific event
   * @param eventName The event from the EulerStateEvents enum in Consts.ts to subscribe to
   * @param listener The listener function to invoke when the event is triggered
   */
  public subscribeToEvent(eventName: string, listener: () => void): void {
    // Ensure that eventName is one of the allowed events from EulerStateEvents
    if (!Object.values(EulerStateEvents).includes(eventName)) {
      throw new Error("Invalid event name");
    }
    const listeners = this.getListenersArray(eventName);
    if (listeners && !listeners.includes(listener)) {
      listeners.push(listener);
    }
  }

  public unsubscribeFromEvent(eventName: string, listener: () => void): void {
    // Ensure that eventName is one of the allowed events from EulerStateEvents
    if (!Object.values(EulerStateEvents).includes(eventName)) {
      throw new Error("Invalid event name");
    }
    const listeners = this.getListenersArray(eventName);
    if (listeners) {
      const index = listeners.indexOf(listener);
      if (index !== -1) {
        listeners.splice(index, 1);
      }
    }
  }

  private getListenersArray(eventName: string): (() => void)[] | null {
    switch (eventName) {
      case EulerStateEvents.ON_ENTER_GET_CURRENT_EVENT_STATE:
        return this.onEnterGetCurrentEventStateListeners;
      case EulerStateEvents.ON_ENTER_GET_NEXT_EVENT_STATE:
        return this.onEnterGetNextEventStateListeners;
      case EulerStateEvents.ON_ENTER_LOAD_AND_SHOW_ODDS_STATE:
        return this.onEnterLoadAndShowsOddsStateListeners;
      case EulerStateEvents.ON_ENTER_NO_MORE_BETS_STATE:
        return this.onEnterNoMoreBetsStateListeners;
      case EulerStateEvents.ON_UPDATE_PLAYING_STATE:
        return this.onEnterUpdatePlayingStateListeners;
      case EulerStateEvents.ON_EVENT_PLAYING_STATE:
        return this.onEnterEventPlayingStateListeners;
      case EulerStateEvents.ON_EVENT_END_STATE:
        return this.onEnterEventEndStateListeners;
      default:
        return null; // Return null or throw an error if the event name doesn't match
    }
  }

  public getStateByName(stateName: string): BaseState<EulerStateManager> {
    let stateToReturn: BaseState<EulerStateManager>;
    switch (stateName) {
      case EulerStates.GET_CURRENT_EVENT_STATE:
        stateToReturn = this.eulerGetCurrentEventState;
        break;
      case EulerStates.GET_NEXT_EVENT_STATE:
        stateToReturn = this.eulerGetNextEventState;
        break;
      case EulerStates.LOAD_AND_SHOW_ODDS_STATE:
        stateToReturn = this.eulerLoadAndShowOddsState;
        break;
      case EulerStates.NO_MORE_BETS_STATE:
        stateToReturn = this.eulerNoMoreBetsState;
        break;
      case EulerStates.UPDATE_PLAYING_STATE:
        stateToReturn = this.eulerUpdatePlayingState;
        break;
      case EulerStates.EVENT_PLAYING_STATE:
        stateToReturn = this.eulerEventPlayingState;
        break;
      case EulerStates.EVENT_END_STATE:
        stateToReturn = this.eulerEventEndState;
        break;
      default:
        throw new Error("Invalid state name");
        break;
    }

    return stateToReturn;
  }

  public changeState(newState: BaseState<EulerStateManager>) {
    if (this.currentState) {
      this.currentState.onExit();
    }

    this.currentState = newState;
    this.currentState.onEnter(this); // Pass the StateManager reference to the state.

    // Invoke the event listeners for the current state
    // this.invokeEventListenersForState(newState); //NOTE : Could run event listeners here instead of in the state onEnter method?
  }

  public getCurrentState(): BaseState<EulerStateManager> | null {
    return this.currentState;
  }

  public getCurrentStateID(): BaseState<EulerStateManager>["id"] | null {
    return this.currentState?.id ?? null;
  }

  /*
   * Functions to invoke the event listeners for each state
   */

  public onEnterGetCurrentEventState() {
    if (this.onEnterGetCurrentEventStateListeners) {
      this.onEnterGetCurrentEventStateListeners.forEach((listener) =>
        listener(),
      );
    }
  }

  public onEnterGetNextEventState() {
    if (this.onEnterGetNextEventStateListeners) {
      this.onEnterGetNextEventStateListeners.forEach((listener) => listener());
    }
  }

  public onEnterLoadAndShowOddsState() {
    if (this.onEnterLoadAndShowsOddsStateListeners) {
      this.onEnterLoadAndShowsOddsStateListeners.forEach((listener) =>
        listener(),
      );
    }
  }

  public onEnterNoMoreBetsState() {
    if (this.onEnterNoMoreBetsStateListeners) {
      this.onEnterNoMoreBetsStateListeners.forEach((listener) => listener());
    }
  }

  public onEnterUpdatePlayingState() {
    if (this.onEnterUpdatePlayingStateListeners) {
      this.onEnterUpdatePlayingStateListeners.forEach((listener) => listener());
    }
  }

  public onEnterEventPlayingState() {
    if (this.onEnterEventPlayingStateListeners) {
      this.onEnterEventPlayingStateListeners.forEach((listener) => listener());
    }
  }

  public onEnterEventEndState() {
    if (this.onEnterEventEndStateListeners) {
      this.onEnterEventEndStateListeners.forEach((listener) => listener());
    }
  }
}

export { EulerStateManager };
